import { catchError, filter, of, switchMap, map, distinctUntilChanged } from 'rxjs';
import { ofType } from 'redux-observable';
import facetsActionTypes from '../constants/actionTypeGroupsConstants';
import LocationType from '../enums/locationType';
import ResultType from '../enums/resultType';
import ProductType from '../enums/productType';
import getCounts$ from '../apis/getCounts';
import getBrands$ from '../apis/getBrands';
import { facetsSlice, searchParametersSlice } from './slices';
import { facetsSelector, selectResultType, selectSearchParameters } from './selectors';
import FetchRequest from '../enums/fetchRequest';
import validateIfFetchRequest from '../services/fetchingValidator';
import getDefaultResultType from '../services/resultTypeService';
import getDefaultProductType from '../services/productTypeService';

const actions = searchParametersSlice.actions;

// if the action is of type actions.fetchLocations.type, make sure that the location type is not 'Community'
// to prevent fetching new results when being redirected to the detail page.
const isCommunityLocationTypeSearch = (action) =>
  action.type === actions.fetchLocations.type &&
  action.payload.locationType.toLowerCase() === LocationType.Community.toString();

const isBuilderResultType = (resultType) => resultType === ResultType.Builders.toString();

const isBuilderResultWithRestrictedLocationType = (state) => {
  const resultType = selectResultType(state);
  const locationType = state?.searchParameters?.location?.locationType;
  const restrictedLocationTypes = [
    LocationType.Zip.toString(),
    LocationType.Neighborhood.toString(),
    LocationType.SchoolDistrict.toString()
  ];
  return isBuilderResultType(resultType) && restrictedLocationTypes.includes(locationType);
};

// Epic to be able to know when the fetchResultListings should be executed. In other words, it checks all
// the actions and verify if it should execute the fetchResultListings action.
export const fetchResultListingsEpic = (action$) =>
  action$.pipe(
    filter((action) => validateIfFetchRequest(action.type, FetchRequest.Listings)),
    // if the action is of type actions.fetchLocations.type, make sure that the location type is not 'Community'
    // to prevent fetching new results when being redirected to the detail page.
    filter((action) => !isCommunityLocationTypeSearch(action)),
    switchMap(() => of(actions.fetchResultListings()))
  );

// Epic to be able to retrieve the result count information by checking when the fetchResultCounts action
// was executed. Then, according to the retrieving status, executes the action according to it.
// If it gets successful, it means this call was fulfilled, otherwise it means there was an error.
export const getResultCountsEpic = (action$, state$) =>
  action$.pipe(
    ofType(actions.fetchResultCounts.type),
    switchMap(() => {
      const payload = selectSearchParameters(state$.value);
      return getCounts$(payload).pipe(
        switchMap((data) => of(actions.fetchResultsCountsFulfilled(data))),
        catchError((error) => of(actions.fetchResultsCountsError(error)))
      );
    })
  );

// Epic to be able to know when the fetchResultCounts should be executed. In other words, it checks all
// the actions and verify if it should execute the fetchResultCounts action.
export const fetchResultCountsEpic = (action$, state$) =>
  action$.pipe(
    filter((action) => validateIfFetchRequest(action.type, FetchRequest.Counts)),
    // location is not zip, Neighborhood, SchoolDistrict when result type is builder
    filter(() => !isBuilderResultWithRestrictedLocationType(state$.value)),
    // if the action is of type actions.fetchLocations.type, make sure that the location type is not 'Community'
    // to prevent fetching new results when being redirected to the detail page.
    filter((action) => !isCommunityLocationTypeSearch(action)),
    switchMap(() => of(actions.fetchResultCounts()))
  );

// Epic to be able to retrieve the result facet information by checking when the fetchBrandsFaceting action
// was executed. Then, according to the retrieving status, executes the action according to it.
// If it gets successful, it means this call was fulfilled, otherwise it means there was an error.
export const getResultFacetsEpic = (action$, state$) =>
  action$.pipe(
    ofType(actions.fetchBrandsFaceting.type),
    switchMap(() => {
      const parameters = selectSearchParameters(state$.value);
      return getBrands$(parameters).pipe(
        switchMap((data) => of(actions.fetchResultFacetsFulfilled(data))),
        catchError((error) => of(actions.fetchResultFacetsError(error)))
      );
    })
  );

// Epic to be able to know when the fetchBrandsFaceting should be executed. In other words, it checks all
// the actions and verify if it should execute the fetchBrandsFaceting action.
export const fetchResultFacetsEpic = (action$, state$) =>
  action$.pipe(
    filter((action) => validateIfFetchRequest(action.type, FetchRequest.SchoolsAndBrandsFaceting)),
    // if the action is of type actions.fetchLocations.type, make sure that the location type is not 'Community'
    // to prevent fetching new results when being redirected to the detail page.
    filter((action) => !isCommunityLocationTypeSearch(action)),
    // As the faceting is not being shown when the builder result type is shown, we don't have to keep doing
    // result facets calls while it is builder result type.
    filter(() => !isBuilderResultType(selectResultType(state$.value))),
    switchMap(() => of(actions.fetchBrandsFaceting()))
  );

const filters = facetsSlice.actions;

// Gets the current state of the facets using a selector
function getFacetsStatus(state) {
  return facetsSelector(state);
}

/**
 * Epic that updates the filters when any of the facets actions is dispatched
 * @param {*} action$
 * @param {*} state$
 * @returns a new action of type updateFilters, with the payload being the current state of the facets
 */
export const updateFacetsEpic = (action$, state$) =>
  action$.pipe(
    filter((action) => facetsActionTypes.includes(action.type)),
    // if the action is of type actions.fetchLocations.type, make sure that the location type is not 'Community'
    // to prevent fetching new results when being redirected to the detail page.
    filter((action) => !isCommunityLocationTypeSearch(action)),
    map(() => ({
      type: filters.updateFilters.type,
      payload: getFacetsStatus(state$.value),
    }))
  );
/**
 * Epic that updates the location when result type is changed to Builder directory.
 * By biz rule, when there is a tab change (result type) to builder directory, with a zip location context,
 * it should be redirected to its city
 * @param {*} action$
 * @param {*} state$
 */
export const updateZipSearchesOnBuildersResult = (action$, state$) =>
  action$.pipe(
    ofType(actions.fetchResultsType.type),
    filter((action) => action.payload.resultType === ResultType.Builders.toString()),
    filter(() => state$.value?.searchParameters.location.locationType === LocationType.Zip.toString()),
    distinctUntilChanged(),
    switchMap(() => of(actions.changeZipSearchtoCitySearch()))
  );

  /**
 * Epic that updates the location when result type is changed to Builder directory.
 * By biz rule, when there is a tab change (result type) to builder directory, with a zip location context,
 * it should be redirected to its city
 * @param {*} action$
 * @param {*} state$
 */
export const updateNeighborhoodSearchesOnBuildersResult = (action$, state$) =>
  action$.pipe(
    ofType(actions.fetchResultsType.type),
    filter((action) => action.payload.resultType === ResultType.Builders.toString()),
    filter(() => state$.value?.searchParameters.location.locationType === LocationType.Neighborhood.toString()),
    distinctUntilChanged(),
    switchMap(() => of(actions.changeNeighborhoodSearchtoCitySearch()))
  );

/**
 * Epic that updates the location when result type is changed to Builder directory.
 * By biz rule, when there is a tab change (result type) to builder directory, with a school location context,
 * it should be redirected to its market
 * @param {*} action$
 * @param {*} state$
 */
export const updateSchoolDistrictSearchesOnBuildersResult = (action$, state$) =>
  action$.pipe(
    ofType(actions.fetchResultsType.type),
    filter((action) => action.payload.resultType === ResultType.Builders.toString()),
    filter(() => state$.value?.searchParameters.location.locationType === LocationType.SchoolDistrict.toString()),
    distinctUntilChanged(),
    switchMap(() => of(actions.changeSchoolDistrictSearchtoMarketSearch()))
  );

/**
 * Epic that resets the product type away from Custom/BuildOnYourLot when navigating to State SRP.
 * By biz rule, Custom Build Homes is not available on State SRP.
 * @param {*} action$
 * @param {*} state$
 */
export const resetCustomProductTypeOnStateSearch = (action$, state$) =>
  action$.pipe(
    ofType(actions.fetchLocations.type),
    filter((action) => action.payload.locationType.toLowerCase() === LocationType.State.toString()),
    filter(() => {
      const productType = state$.value?.searchParameters?.product?.productType;
      return productType === ProductType.Custom.toString() ||
             productType === ProductType.BuildOnYourLot.toString();
    }),
    distinctUntilChanged(),
    switchMap(() => {
      const currentResultType = state$.value?.searchParameters?.results?.resultType;
      const currentProductType = state$.value?.searchParameters?.product?.productType;
      return of(actions.allOptions({
        resultType: getDefaultResultType(currentResultType),
        productType: getDefaultProductType(currentProductType, currentResultType),
        homeStatus: null,
      }));
    })
  );